import { ref } from "vue";
import { POLL_SUPERSEDED, useTaskPoll } from "@/composables/useTaskPoll";
export { POLL_SUPERSEDED };
export const PACKET_REVIEW_FAILED = "Could not check this packet for blank forms or missing documents — check it before sending.";
export const PACKET_REVIEW_UNAVAILABLE = "This packet changed and was not checked for blank forms or missing documents — check it before sending.";
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
/**
 * Poll + packet-review state machine shared by the order-page appeal letter and the Appeals page.
 *
 * `fetchTask(taskId)` performs one poll of the screen's own draft/review endpoint. The machine
 * guards three races: an older review finishing after a newer one (the flags generation counter),
 * a superseded review id the backend 404s on purpose (mapped to PACKET_REVIEW_UNAVAILABLE, not an
 * error), and a letter row re-bound to a newer review while this one ran (followed via re-bind).
 */
export function useAppealPacketReview(fetchTask) {
    const packetFlags = ref([]);
    const packetReviewPending = ref(false);
    const packetReviewError = ref("");
    const { pollTask, clearPolls } = useTaskPoll(fetchTask, { intervalMs: POLL_INTERVAL_MS, timeoutMs: POLL_TIMEOUT_MS });
    let packetFlagsGeneration = 0;
    let packetSaveSequence = 0;
    let polledReviewSequence = 0;
    const bumpFlagsGeneration = () => ++packetFlagsGeneration;
    const nextSaveSequence = () => ++packetSaveSequence;
    const assignPacketFlags = (generation, flags) => {
        if (generation === packetFlagsGeneration)
            packetFlags.value = flags || [];
    };
    const refreshPacketFlags = (reviewTaskId) => {
        const generation = ++packetFlagsGeneration;
        packetReviewPending.value = true;
        packetReviewError.value = "";
        const settle = (error) => {
            if (generation !== packetFlagsGeneration)
                return;
            packetReviewPending.value = false;
            packetReviewError.value = error;
        };
        return pollTask(reviewTaskId, () => generation === packetFlagsGeneration)
            .then((result) => {
            if (result.status === "done") {
                assignPacketFlags(generation, result.packetFlags);
                // The row may have been re-bound to a newer review while this one ran: follow it.
                const bound = result.reviewTaskId;
                if (bound && bound !== reviewTaskId && generation === packetFlagsGeneration) {
                    refreshPacketFlags(bound);
                }
                else {
                    settle(result.reviewUnavailable ? PACKET_REVIEW_UNAVAILABLE : "");
                }
            }
            else if (result.status === POLL_SUPERSEDED) {
                settle("");
            }
            else {
                settle(PACKET_REVIEW_FAILED);
            }
        })
            .catch((e) => {
            // The backend 404s a superseded review id on purpose: the packet changed again.
            settle(e?.response?.status === 404 ? PACKET_REVIEW_UNAVAILABLE : PACKET_REVIEW_FAILED);
        });
    };
    const trackPacketReview = (reviewTaskId, sequence, reviewUnavailable) => {
        if (sequence < polledReviewSequence)
            return;
        if (reviewTaskId) {
            polledReviewSequence = sequence;
            refreshPacketFlags(reviewTaskId);
            return;
        }
        if (!reviewUnavailable)
            return;
        polledReviewSequence = sequence;
        packetFlagsGeneration += 1;
        packetReviewPending.value = false;
        packetReviewError.value = PACKET_REVIEW_UNAVAILABLE;
    };
    return {
        packetFlags,
        packetReviewPending,
        packetReviewError,
        pollTask,
        clearPolls,
        assignPacketFlags,
        bumpFlagsGeneration,
        nextSaveSequence,
        trackPacketReview,
    };
}
